import { useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { firstToUpper } from '@/helpers/string';
import clsx from 'clsx';
import {
    Typography,
    Button,
    Row,
    Col,
    Space,
    Dropdown,
    Divider,
} from 'antd';
import BlockDescription from '@/components/Information/WithImage/Block';
import SimpleDropdownMenu from '@/components/SimpleDropdownMenu';
import { actions } from '@/redux/tour/packages/delete/reducer';
import { actions as shareActions } from '@/redux/b2b/share/reducer';
import { selectors } from '@/redux/b2b/share/model';
import {
    BuildingIcon,
    CalendarIconBlue,
    MoreOutlinedIcon,
    PlaneIconRight,
} from '@/assets/images/icons/screens';
import { TransferIcon } from '@/assets/images/icons/sidebar';
import TourPriceAndB2bSection from '@/components/TourPriceAndB2bSection';
import useNotification from '@/hooks/useNotification';
import decodeHtml from '@/helpers/decodeHtml';

const GeneralData = ({
    data,
    myService,
}) => {
    const { t } = useTranslation();
    const { push } = useHistory();
    const dispatch = useDispatch();

    const editUrl = `/app/tours/packages/${data.id}/edit`;

    const onEdit = useCallback(() => {
        push(editUrl);
    }, [push, editUrl]);

    // Message from delete action
    const messageSuccess = useSelector((state) => state.tour.packages.delete.messageSuccess);
    const error = useSelector((state) => state.tour.packages.delete.error);

    useNotification({
        messageSuccess,
        error,
        item: data.name,
        clearAction: actions.clearDeletePackageState(),
        mode: 'view',
        pushUrl: '/app/tours/packages',
    });

    // Message from share/remove actions
    const message = useSelector(selectors.message);
    const [greenButton, setGreenButton] = useState(data.share);

    useEffect(() => {
        if (message === 'Shared successfully') {
            setGreenButton(true);
        }
        if (message === 'Removed successfully') {
            setGreenButton(false);
            dispatch(shareActions.clearShareRemoveMessage());
        }
    }, [message, dispatch]);

    return (
        <div className="general-data">
            <Row gutter={[0, 10]}>
                <Col
                    lg={14}
                >
                    <Typography.Text className="tour-description">
                        <div dangerouslySetInnerHTML={{ __html: decodeHtml(data.description) }} />
                    </Typography.Text>
                </Col>

                <Col lg={1}>
                    <Divider type="vertical" dashed />
                </Col>

                <Col lg={9}>
                    <Space className="price-section">
                        <TourPriceAndB2bSection
                            myService={myService}
                            data={data}
                            greenButton={greenButton}
                        />

                        {myService && (
                            <Dropdown
                                trigger={['click']}
                                placement="bottom"
                                overlayClassName={clsx(
                                    'edit-delete-dropdown',
                                    'edit-delete-dropdown-with-text',
                                )}
                                menu={
                                    {
                                        items: SimpleDropdownMenu({
                                            editText: t('Edit this package'),
                                            deleteText: t('Delete this package'),
                                            type: 'package',
                                            onEditClick: onEdit,
                                            deleteAction: actions.requestDeletePackage(data.id),
                                            removeSharedText: t('Remove this shared package'),
                                            removeSharedServiceAction:
                                                shareActions.requestRemoveSharedService(
                                                    data.id,
                                                    { type: 1 },
                                                ),
                                            removeSharedCollectedText: t('Remove this shared collected package'),
                                            removeSharedCollectedServiceAction:
                                            shareActions.requestRemoveSharedCollectedService(
                                                data.id,
                                                { type: 1 },
                                            ),
                                            isShared: greenButton,
                                        }),
                                    }
                                }
                            >
                                <Button type="text">
                                    <MoreOutlinedIcon />
                                </Button>
                            </Dropdown>
                        )}
                    </Space>
                </Col>
            </Row>

            <Divider />

            <Row justify="space-between" className="accomodation-block-details">
                <Col xl={8} lg={12} md={8} sm={12} xs={24}>
                    <BlockDescription
                        spaceSize={14}
                        icon={<BuildingIcon />}
                        label={t('Accommodation')}
                        content={`${data.hotel} ${data.hotel_location}`}
                    />
                </Col>

                <Col xl={8} lg={12} md={8} sm={12} xs={24}>
                    <BlockDescription
                        spaceSize={14}
                        icon={<CalendarIconBlue />}
                        label={t('Duration')}
                        content={`
                            ${data.duration}
                            ${data.duration_type === 'D' ? (
            data.duration === 1 ? 'Day' : 'Days'
        ) : (
            data.duration === 1 ? 'Hour' : 'Hours'
        )}`}
                    />
                </Col>

                <Col xl={8} lg={12} md={8} sm={12} xs={24}>
                    <BlockDescription
                        spaceSize={14}
                        icon={<TransferIcon />}
                        label={t('Tour Style')}
                        content={data.style.map((style) => t(firstToUpper(style.name))).join(', ')}
                    />
                </Col>
            </Row>

            <Divider />

            <Row justify="start" className="accomodation-block-details">
                {data.extra_services.map((extra) => (
                    <Col key={extra.id} md={8} sm={12} xs={24}>
                        <BlockDescription
                            spaceSize={14}
                            icon={<PlaneIconRight />}
                            label={`
                            ${extra.name}
                            ${extra.price === '0.00' ? `(${t('inclusive')})` : `(${extra.price} / ${extra.price_type})`}`}
                            content={`${t('Regular or charter depending on departures')}.`}
                        />
                    </Col>
                ))}
            </Row>
        </div>
    );
};

export default GeneralData;
